import { PersonEditOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Avatar, InfoPill, Stack, Typography } from '@lyearn/ui';
import useNavigation from '@/hooks/useNavigation';
import { avatars } from '@/modules/session/utils/getRandomAvatar';
import routes from '@/pages/corporate/routes';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';

import EmptyState from '../../../KnowledgeAssetDetails/components/EmptyState';
import { useKnowledgeAssetsQuery } from '../../../KnowledgeAssetManager/graphql/query/generated/knowledgeAssets';

interface KnowledgeAssetType {
  id: string;
  name: string;
  description?: string;
  cover?: {
    url?: string;
  };
  childKnowledgeAssets?: Array<{
    id: string;
    name: string;
    cover?: {
      url?: string;
    };
  }>;
}

interface PersonaJourneyPageProps {
  solutionName: string;
  targetPersonas: {
    practitioners: Array<{
      node?: {
        id?: string;
        name?: string;
        cover?: {
          url?: string;
        };
      };
    }>;
    executiveBuyers: Array<{
      node?: {
        id?: string;
        name?: string;
        cover?: {
          url?: string;
        };
      };
    }>;
    decisionMakers: Array<{
      node?: {
        id?: string;
        name?: string;
        cover?: {
          url?: string;
        };
      };
    }>;
  };
  personaJourney?: string;
}

const PersonaJourneyPage = ({
  solutionName,
  targetPersonas,
  personaJourney,
}: PersonaJourneyPageProps) => {
  const isAdminRoute = useAdminRoute();
  const navigation = useNavigation();
  const journeyIds =
    personaJourney
      ?.split('|')
      .filter(Boolean)
      .flatMap((id) => id.split(',').map((i) => i.trim()))
      .filter(Boolean) || [];

  const [{ data: journeyData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: journeyIds,
      },
    },
    pause: !journeyIds.length,
  });

  const journeyPersonas = (journeyData?.contents?.edges || []) as Array<{
    node: KnowledgeAssetType;
  }>;

  const onClickSolutionJourneyCard = (id: string) => {
    let path = routes.KnowledgeAssetLearnerDetails(id, 'persona-journey');
    if (isAdminRoute) {
      path = routes.KnowledgeAssetDetails(id, 'persona-journey');
    }
    console.log(id, 'id-123');

    navigation.push(path);
  };

  const onClickPersonasCard = (id?: string) => {
    if (!id) return;
    let path = routes.KnowledgeAssetLearnerDetails(id, 'persona');
    if (isAdminRoute) {
      path = routes.KnowledgeAssetDetails(id, 'persona');
    }
    navigation.push(path);
  };
  return (
    <Stack className="max-w-[101.2rem] min-w-[75rem] mx-64 mt-48 mb-128" direction="col" gapY={21}>
      <Typography variant="display-xl">Persona & Journey</Typography>

      {/* Target Persona section */}
      <Stack direction="col" gapY={21}>
        <Typography variant="display-m">Target Persona</Typography>
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <PersonEditOutline fillColorToken="icon-active" height={23} width={23} />
            <Typography variant="heading-xs">Personas</Typography>
          </Stack>
          {targetPersonas.practitioners.length === 0 &&
          targetPersonas.decisionMakers.length === 0 &&
          targetPersonas.executiveBuyers.length === 0 ? (
            <EmptyState />
          ) : (
            <Stack className="grid grid-cols-3 gap-16">
              <Stack className="w-full" direction="col">
                <Stack className="mb-8">
                  <Typography variant="body-short-02"> Practitioner </Typography>
                </Stack>

                <Stack className="border border-border rounded-8" direction="col">
                  {targetPersonas.practitioners.map((persona, index) => (
                    <Stack
                      key={index}
                      alignItems="center"
                      className={cn(
                        'p-16 cursor-pointer',
                        index !== targetPersonas.practitioners.length - 1 &&
                          'border-b border-border',
                      )}
                      direction="row"
                      gapX={12}
                      onClick={() => onClickPersonasCard(persona.node?.id)}>
                      <Avatar
                        size="large"
                        src={persona?.node?.cover?.url || avatars[index % 100]?.url}
                      />
                      <Stack direction="col">
                        <Typography variant="label-02">{persona?.node?.name}</Typography>
                        <Typography className="text-text-secondary" variant="body-short-03">
                          Practitioner
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>

              <Stack className="w-full" direction="col">
                <Stack className="mb-8">
                  <Typography variant="body-short-02"> Decision Maker </Typography>
                </Stack>

                <Stack className="border border-border rounded-8" direction="col">
                  {targetPersonas.decisionMakers.map((persona, index) => (
                    <Stack
                      key={index}
                      alignItems="center"
                      className={cn(
                        'p-16 cursor-pointer',
                        index !== targetPersonas.decisionMakers.length - 1 &&
                          'border-b border-border',
                      )}
                      direction="row"
                      gapX={12}
                      onClick={() => onClickPersonasCard(persona.node?.id)}>
                      <Avatar
                        size="large"
                        src={persona?.node?.cover?.url || avatars[(index + 2) % 100]?.url}
                      />
                      <Stack direction="col">
                        <Typography variant="label-02">{persona?.node?.name}</Typography>
                        <Typography className="text-text-secondary" variant="body-short-03">
                          Decision Maker
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>

              <Stack className="w-full" direction="col">
                <Stack className="mb-8">
                  <Typography variant="body-short-02"> Executive Buyer </Typography>
                </Stack>

                <Stack className="border border-border rounded-8" direction="col">
                  {targetPersonas.executiveBuyers.map((persona, index) => (
                    <Stack
                      key={index}
                      alignItems="center"
                      className={cn(
                        'p-16 cursor-pointer',
                        index !== targetPersonas.executiveBuyers.length - 1 &&
                          'border-b border-border',
                      )}
                      direction="row"
                      gapX={12}
                      onClick={() => onClickPersonasCard(persona.node?.id)}>
                      <Avatar
                        size="large"
                        src={persona?.node?.cover?.url || avatars[(index + 1) % 100]?.url}
                      />
                      <Stack direction="col">
                        <Typography variant="label-02">{persona?.node?.name}</Typography>
                        <Typography className="text-text-secondary" variant="body-short-03">
                          Executive Buyer
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* Solution Journey section */}
      {journeyPersonas.length > 0 && (
        <Stack direction="col" gapY={21}>
          <Typography variant="display-m">Solution Journey</Typography>
          <Stack className="grid grid-cols-2 gap-16">
            {journeyPersonas.map((journey, index) => {
              const childPersona = journey?.node?.childKnowledgeAssets?.[0];
              const childNames = journey?.node?.childKnowledgeAssets
                ?.map((persona) => persona.name)
                .join(' / ');

              return (
                <Stack
                  key={index}
                  className="min-h-[15.2rem] rounded-12 border border-border p-20 cursor-pointer"
                  direction="col"
                  gapY={16}
                  justifyContent="between"
                  onClick={() => onClickSolutionJourneyCard(journey.node.id)}>
                  <Stack direction="col" gapY={8}>
                    <Typography variant="heading-xs">{journey?.node?.name}</Typography>
                    <Typography
                      className="text-text-secondary"
                      numberOfLines={2}
                      variant="label-02">
                      {journey?.node?.description}
                    </Typography>
                  </Stack>
                  <Stack alignItems="start">
                    {childPersona ? (
                      <InfoPill
                        appearance="secondary"
                        className="py-4 px-10"
                        label={childNames || ''}
                        size="large"
                        src={childPersona.cover?.url || avatars[index % 100]?.url}
                        type="avatar"
                      />
                    ) : (
                      <InfoPill
                        appearance="secondary"
                        className="py-4 px-10"
                        label="No persona tagged"
                        size="large"
                        type="text"
                      />
                    )}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default PersonaJourneyPage;
